import React from 'react';
import { Clock, CheckCircle, LogOut, Loader2 } from 'lucide-react';
import { useLanguageStore } from '../../store/languageStore';
import translations from '../../translations';

interface ActionButtonProps {
  status: 'not_clocked_in' | 'working' | 'job_finished' | 'clocked_out';
  loading: boolean;
  hasActiveTasks: boolean;
  onAction: () => void;
}

export function ActionButton({ status, loading, hasActiveTasks, onAction }: ActionButtonProps) {
  const { language } = useLanguageStore();
  const t = translations[language].timeTracker;

  // Don't render anything if clocked out
  if (status === 'clocked_out') {
    return null;
  }

  if (loading) {
    return (
      <button
        disabled
        className="w-full bg-gray-400 dark:bg-gray-600 text-white rounded-lg py-4 px-6 text-lg flex items-center justify-center gap-2"
      >
        <Loader2 className="h-5 w-5 animate-spin" />
        {t.buttons.processing}
      </button>
    );
  }

  switch (status) {
    case 'not_clocked_in':
      return (
        <button
          onClick={onAction}
          className="w-full bg-green-600 hover:bg-green-700 text-white rounded-lg py-4 px-6 text-lg flex items-center justify-center gap-2"
        >
          <Clock className="h-6 w-6" />
          {t.actions.clockIn}
        </button>
      );
    case 'working':
      return (
        <button
          onClick={onAction}
          disabled={loading || hasActiveTasks}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-4 px-6 text-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <CheckCircle className="h-6 w-6" />
          {hasActiveTasks ? t.actions.completeActiveTasks : t.actions.finishJob}
        </button>
      );
    case 'job_finished':
      return (
        <button
          onClick={onAction}
          disabled={hasActiveTasks}
          className="w-full bg-red-600 hover:bg-red-700 text-white rounded-lg py-4 px-6 text-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <LogOut className="h-6 w-6" />
          {t.actions.clockOut}
        </button>
      );
    default:
      return null;
  }
}